import React, { useState, useEffect } from 'react';
import { 
  Users, Shield, Crown, Trash2, Edit, Plus, Search, 
  CheckCircle, XCircle, TrendingUp, Calendar, User,
  ChevronDown, MoreHorizontal, Zap, Ticket, Megaphone,
  Copy, Send, Eye, Gift, CreditCard, Settings, RefreshCw, Loader2
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from '../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../components/ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
} from '../components/ui/dropdown-menu';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { useToast } from '../hooks/use-toast';
import axios from 'axios';
import { cardAdminAPI } from '../services/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const AdminPanel = () => {
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [coupons, setCoupons] = useState([]);
  const [broadcasts, setBroadcasts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [isCreateCouponOpen, setIsCreateCouponOpen] = useState(false);
  const [isBroadcastDialogOpen, setIsBroadcastDialogOpen] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [sendingBroadcast, setSendingBroadcast] = useState(false);
  const { toast } = useToast();

  const [formData, setFormData] = useState({
    email: '',
    name: '',
    role: 'owner',
    is_verified: true,
    subscription_tier: 'free'
  });

  const [couponFormData, setCouponFormData] = useState({
    code: '',
    discount_type: 'free_upgrade',
    discount_value: 100,
    max_uses: 1,
    valid_days: 30,
    description: ''
  });

  const [broadcastFormData, setBroadcastFormData] = useState({
    subject: '',
    message: '',
    target_audience: 'all'
  });

  const [editUserData, setEditUserData] = useState(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [usersRes, statsRes, couponsRes, broadcastsRes] = await Promise.all([
        axios.get(`${API}/admin/users`, { withCredentials: true }),
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/admin/coupons`, { withCredentials: true }),
        axios.get(`${API}/admin/broadcasts`, { withCredentials: true })
      ]);
      setUsers(usersRes.data);
      setStats(statsRes.data);
      setCoupons(couponsRes.data || []);
      setBroadcasts(broadcastsRes.data || []);
    } catch (error) {
      console.error('Error fetching admin data:', error);
      if (error.response?.status === 403) {
        toast({ title: 'Access denied. Super admin required.', variant: 'destructive' });
      } else {
        toast({ title: 'Error loading admin data', variant: 'destructive' });
      }
    } finally {
      setLoading(false);
    }
  };

  // Coupon functions
  const handleCreateCoupon = async (e) => {
    e.preventDefault();
    try {
      await axios.post(`${API}/admin/coupons`, couponFormData, { withCredentials: true });
      toast({ title: 'Coupon created successfully!' });
      setIsCreateCouponOpen(false);
      setCouponFormData({ code: '', discount_type: 'free_upgrade', discount_value: 100, max_uses: 1, valid_days: 30, description: '' });
      fetchData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error creating coupon', variant: 'destructive' });
    }
  };

  const handleDeleteCoupon = async (couponId) => {
    try {
      await axios.delete(`${API}/admin/coupons/${couponId}`, { withCredentials: true });
      toast({ title: 'Coupon deleted' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error deleting coupon', variant: 'destructive' });
    }
  };

  const handleToggleCoupon = async (couponId) => {
    try {
      await axios.put(`${API}/admin/coupons/${couponId}/toggle`, {}, { withCredentials: true });
      toast({ title: 'Coupon status updated' });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating coupon', variant: 'destructive' });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({ title: 'Copied to clipboard!' });
  };

  // Broadcast functions
  const handleSendBroadcast = async (e) => {
    e.preventDefault();
    if (!broadcastFormData.subject || !broadcastFormData.message) {
      toast({ title: 'Please fill in subject and message', variant: 'destructive' });
      return;
    }
    
    setSendingBroadcast(true);
    try {
      const response = await axios.post(`${API}/admin/broadcasts`, broadcastFormData, { withCredentials: true });
      toast({ title: `Broadcast sent to ${response.data.sent_count} users!` });
      setIsBroadcastDialogOpen(false);
      setBroadcastFormData({ subject: '', message: '', target_audience: 'all' });
      fetchData();
    } catch (error) {
      toast({ title: error.response?.data?.detail || 'Error sending broadcast', variant: 'destructive' });
    } finally {
      setSendingBroadcast(false);
    }
  };

  const handleCreateUser = async (e) => {
    e.preventDefault();
    try {
      // Create user first
      const createResponse = await axios.post(`${API}/admin/users`, {
        email: formData.email,
        name: formData.name,
        role: formData.role,
        is_verified: formData.is_verified
      }, { withCredentials: true });
      
      // If Pro tier selected, upgrade the subscription
      if (formData.subscription_tier === 'pro' && createResponse.data.id) {
        await axios.put(`${API}/admin/users/${createResponse.data.id}/subscription`, 
          { tier: 'pro' }, 
          { withCredentials: true }
        );
      }
      
      toast({ title: 'User created successfully! They should use Forgot Password to set their password.' });
      setIsCreateDialogOpen(false);
      setFormData({ email: '', name: '', role: 'owner', is_verified: true, subscription_tier: 'free' });
      fetchData();
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || 'Error creating user', 
        variant: 'destructive' 
      });
    }
  };

  const handleEditUser = (user) => {
    setEditUserData({
      id: user.id,
      name: user.name,
      email: user.email,
      role: user.role,
      is_verified: user.is_verified,
      is_active: user.is_active !== false,
      subscription_tier: user.subscription_tier || 'free'
    });
    setIsEditDialogOpen(true);
  };

  const handleUpdateUser = async (e) => {
    e.preventDefault();
    if (!editUserData) return;
    
    try {
      // Update user details
      await axios.put(`${API}/admin/users/${editUserData.id}`, {
        name: editUserData.name,
        role: editUserData.role,
        is_verified: editUserData.is_verified,
        is_active: editUserData.is_active
      }, { withCredentials: true });
      
      // Update subscription if changed
      const currentUser = users.find(u => u.id === editUserData.id);
      if (currentUser && currentUser.subscription_tier !== editUserData.subscription_tier) {
        await axios.put(`${API}/admin/users/${editUserData.id}/subscription`, 
          { tier: editUserData.subscription_tier }, 
          { withCredentials: true }
        );
      }
      
      toast({ title: 'User updated successfully!' });
      setIsEditDialogOpen(false);
      setEditUserData(null);
      fetchData();
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || 'Error updating user', 
        variant: 'destructive' 
      });
    }
  };

  const handleDeleteUser = async () => {
    if (!userToDelete) return;
    try {
      await axios.delete(`${API}/admin/users/${userToDelete.id}`, { withCredentials: true });
      toast({ title: 'User deleted successfully' });
      setDeleteConfirmOpen(false);
      setUserToDelete(null);
      fetchData();
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || 'Error deleting user', 
        variant: 'destructive' 
      });
    }
  };

  const handleUpdateSubscription = async (userId, tier) => {
    try {
      await axios.put(`${API}/admin/users/${userId}/subscription`, { tier }, { withCredentials: true });
      toast({ title: `User upgraded to ${tier} plan` });
      fetchData();
    } catch (error) {
      toast({ 
        title: error.response?.data?.detail || 'Error updating subscription', 
        variant: 'destructive' 
      });
    }
  };

  const handleToggleVerification = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, { 
        is_verified: !user.is_verified 
      }, { withCredentials: true });
      toast({ title: `User ${user.is_verified ? 'unverified' : 'verified'} successfully` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating user', variant: 'destructive' });
    }
  };

  const handleToggleActive = async (user) => {
    try {
      await axios.put(`${API}/admin/users/${user.id}`, { 
        is_active: !user.is_active 
      }, { withCredentials: true });
      toast({ title: `User ${user.is_active ? 'deactivated' : 'activated'} successfully` });
      fetchData();
    } catch (error) {
      toast({ title: 'Error updating user', variant: 'destructive' });
    }
  };

  const filteredUsers = users.filter(user =>
    user.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    user.email?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getRoleBadge = (role) => {
    switch (role) {
      case 'super_admin':
        return <Badge className="bg-red-100 text-red-800"><Shield className="h-3 w-3 mr-1" />Super Admin</Badge>;
      case 'owner':
        return <Badge className="bg-purple-100 text-purple-800"><Crown className="h-3 w-3 mr-1" />Owner</Badge>;
      case 'manager':
        return <Badge className="bg-blue-100 text-blue-800">Manager</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Assistant</Badge>;
    }
  };

  const getTierBadge = (tier) => {
    if (tier === 'enterprise') {
      return <Badge className="bg-gradient-to-r from-indigo-600 to-blue-600 text-white">ENTERPRISE</Badge>;
    }
    if (tier === 'pro') {
      return <Badge className="bg-gradient-to-r from-purple-500 to-indigo-500 text-white">PRO</Badge>;
    }
    return <Badge variant="outline">Free</Badge>;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    return new Date(dateStr).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading admin panel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-800 flex items-center">
            <Shield className="h-8 w-8 mr-3 text-red-600" />
            Admin Panel
          </h1>
          <p className="text-gray-600 mt-1">Manage users, subscriptions, and system settings</p>
        </div>
        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-red-600 hover:bg-red-700">
              <Plus className="h-4 w-4 mr-2" />
              Add User
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>Add a new user to the system. They'll use Forgot Password to set their password.</DialogDescription>
            </DialogHeader>
            <form onSubmit={handleCreateUser} className="space-y-4">
              <div>
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="user@example.com"
                  required
                />
              </div>
              <div>
                <Label htmlFor="name">Name</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Full Name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="role">Role</Label>
                <Select value={formData.role} onValueChange={(v) => setFormData({ ...formData, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="assistant">Assistant</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="subscription">Subscription Plan</Label>
                <Select value={formData.subscription_tier} onValueChange={(v) => setFormData({ ...formData, subscription_tier: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="is_verified"
                  checked={formData.is_verified}
                  onChange={(e) => setFormData({ ...formData, is_verified: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="is_verified">Pre-verified (can login immediately)</Label>
              </div>
              <p className="text-sm text-gray-500">
                The user will need to use "Forgot Password" to set their password.
              </p>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  Create User
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Edit User Dialog */}
      <Dialog open={isEditDialogOpen} onOpenChange={setIsEditDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit User</DialogTitle>
            <DialogDescription>Modify user details, role, and subscription tier.</DialogDescription>
          </DialogHeader>
          {editUserData && (
            <form onSubmit={handleUpdateUser} className="space-y-4">
              <div>
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={editUserData.email}
                  disabled
                  className="bg-gray-100"
                />
              </div>
              <div>
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editUserData.name}
                  onChange={(e) => setEditUserData({ ...editUserData, name: e.target.value })}
                  placeholder="Full Name"
                  required
                />
              </div>
              <div>
                <Label htmlFor="edit-role">Role</Label>
                <Select value={editUserData.role} onValueChange={(v) => setEditUserData({ ...editUserData, role: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="owner">Owner</SelectItem>
                    <SelectItem value="manager">Manager</SelectItem>
                    <SelectItem value="assistant">Assistant</SelectItem>
                    <SelectItem value="super_admin">Super Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="edit-subscription">Subscription Plan</Label>
                <Select value={editUserData.subscription_tier} onValueChange={(v) => setEditUserData({ ...editUserData, subscription_tier: v })}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="free">Free</SelectItem>
                    <SelectItem value="pro">Pro</SelectItem>
                    <SelectItem value="enterprise">Enterprise</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-is_verified"
                  checked={editUserData.is_verified}
                  onChange={(e) => setEditUserData({ ...editUserData, is_verified: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-is_verified">Verified</Label>
              </div>
              <div className="flex items-center space-x-2">
                <input
                  type="checkbox"
                  id="edit-is_active"
                  checked={editUserData.is_active}
                  onChange={(e) => setEditUserData({ ...editUserData, is_active: e.target.checked })}
                  className="w-4 h-4"
                />
                <Label htmlFor="edit-is_active">Active</Label>
              </div>
              <div className="flex justify-end space-x-2">
                <Button type="button" variant="outline" onClick={() => setIsEditDialogOpen(false)}>
                  Cancel
                </Button>
                <Button type="submit" className="bg-red-600 hover:bg-red-700">
                  Save Changes
                </Button>
              </div>
            </form>
          )}
        </DialogContent>
      </Dialog>

      {/* Stats Cards */}
      {stats && (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Users</p>
                  <p className="text-3xl font-bold">{stats.users.total}</p>
                </div>
                <Users className="h-10 w-10 text-blue-500" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.users.verified} verified, {stats.users.unverified} pending
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Paid Subscribers</p>
                  <p className="text-3xl font-bold text-purple-600">{(stats.subscriptions.pro || 0) + (stats.subscriptions.enterprise || 0)}</p>
                </div>
                <Zap className="h-10 w-10 text-purple-500" />
              </div>
              <p className="text-xs text-gray-500 mt-2">
                {stats.subscriptions.pro || 0} Pro · {stats.subscriptions.enterprise || 0} Enterprise · {stats.subscriptions.free} Free
              </p>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Programs</p>
                  <p className="text-3xl font-bold">{stats.content.programs}</p>
                </div>
                <Calendar className="h-10 w-10 text-green-500" />
              </div>
            </CardContent>
          </Card>
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">Total Athletes</p>
                  <p className="text-3xl font-bold">{stats.content.athletes}</p>
                </div>
                <User className="h-10 w-10 text-orange-500" />
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Search */}
      <Tabs defaultValue="users" className="space-y-4">
        <TabsList className="grid w-full grid-cols-4">
          <TabsTrigger value="users" className="flex items-center gap-2">
            <Users className="h-4 w-4" />
            Users
          </TabsTrigger>
          <TabsTrigger value="coupons" className="flex items-center gap-2">
            <Ticket className="h-4 w-4" />
            Coupons
          </TabsTrigger>
          <TabsTrigger value="broadcasts" className="flex items-center gap-2">
            <Megaphone className="h-4 w-4" />
            Broadcasts
          </TabsTrigger>
          <TabsTrigger value="payments" className="flex items-center gap-2">
            <CreditCard className="h-4 w-4" />
            Payments
          </TabsTrigger>
        </TabsList>

        {/* Users Tab */}
        <TabsContent value="users" className="space-y-4">
          <div className="flex items-center space-x-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
              <Input
                type="text"
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
              />
            </div>
            <p className="text-sm text-gray-500">{filteredUsers.length} users</p>
          </div>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b">
                <tr>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">User</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Role</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Plan</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Usage</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Status</th>
                  <th className="text-left py-3 px-4 text-sm font-medium text-gray-600">Created</th>
                  <th className="text-right py-3 px-4 text-sm font-medium text-gray-600">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {filteredUsers.map((user) => (
                  <tr key={user.id} className="hover:bg-gray-50">
                    <td className="py-3 px-4">
                      <div>
                        <p className="font-medium text-gray-900">{user.name}</p>
                        <p className="text-sm text-gray-500">{user.email}</p>
                      </div>
                    </td>
                    <td className="py-3 px-4">{getRoleBadge(user.role)}</td>
                    <td className="py-3 px-4">{getTierBadge(user.subscription_tier)}</td>
                    <td className="py-3 px-4">
                      <p className="text-sm text-gray-600">
                        {user.usage?.programs || 0} programs, {user.usage?.athletes || 0} athletes
                      </p>
                    </td>
                    <td className="py-3 px-4">
                      <div className="flex flex-col gap-1">
                        {user.is_verified ? (
                          <Badge className="bg-green-100 text-green-800 w-fit">
                            <CheckCircle className="h-3 w-3 mr-1" />Verified
                          </Badge>
                        ) : (
                          <Badge className="bg-yellow-100 text-yellow-800 w-fit">
                            <XCircle className="h-3 w-3 mr-1" />Unverified
                          </Badge>
                        )}
                        {user.is_active === false && (
                          <Badge className="bg-red-100 text-red-800 w-fit">Deactivated</Badge>
                        )}
                      </div>
                    </td>
                    <td className="py-3 px-4 text-sm text-gray-600">
                      {formatDate(user.created_at)}
                    </td>
                    <td className="py-3 px-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem onClick={() => handleEditUser(user)}>
                            <Edit className="h-4 w-4 mr-2" />
                            Edit User
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          {user.subscription_tier !== 'pro' && user.subscription_tier !== 'enterprise' ? (
                            <>
                              <DropdownMenuItem onClick={() => handleUpdateSubscription(user.id, 'pro')}>
                                <Zap className="h-4 w-4 mr-2 text-purple-600" />
                                Set to Pro
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateSubscription(user.id, 'enterprise')}>
                                <Zap className="h-4 w-4 mr-2 text-indigo-600" />
                                Set to Enterprise
                              </DropdownMenuItem>
                            </>
                          ) : user.subscription_tier === 'pro' ? (
                            <>
                              <DropdownMenuItem onClick={() => handleUpdateSubscription(user.id, 'enterprise')}>
                                <Zap className="h-4 w-4 mr-2 text-indigo-600" />
                                Upgrade to Enterprise
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateSubscription(user.id, 'free')}>
                                <Zap className="h-4 w-4 mr-2" />
                                Downgrade to Free
                              </DropdownMenuItem>
                            </>
                          ) : (
                            <>
                              <DropdownMenuItem onClick={() => handleUpdateSubscription(user.id, 'pro')}>
                                <Zap className="h-4 w-4 mr-2 text-purple-600" />
                                Downgrade to Pro
                              </DropdownMenuItem>
                              <DropdownMenuItem onClick={() => handleUpdateSubscription(user.id, 'free')}>
                                <Zap className="h-4 w-4 mr-2" />
                                Downgrade to Free
                              </DropdownMenuItem>
                            </>
                          )}
                          <DropdownMenuItem onClick={() => handleToggleVerification(user)}>
                            {user.is_verified ? (
                              <><XCircle className="h-4 w-4 mr-2" />Unverify</>
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-2" />Verify</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuItem onClick={() => handleToggleActive(user)}>
                            {user.is_active !== false ? (
                              <><XCircle className="h-4 w-4 mr-2 text-red-600" />Deactivate</>
                            ) : (
                              <><CheckCircle className="h-4 w-4 mr-2 text-green-600" />Activate</>
                            )}
                          </DropdownMenuItem>
                          <DropdownMenuSeparator />
                          <DropdownMenuItem 
                            className="text-red-600"
                            onClick={() => {
                              setUserToDelete(user);
                              setDeleteConfirmOpen(true);
                            }}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete User
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

          {/* Delete Confirmation */}
          <AlertDialog open={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>Delete User</AlertDialogTitle>
                <AlertDialogDescription>
                  Are you sure you want to delete <strong>{userToDelete?.name}</strong> ({userToDelete?.email})?
                  <br /><br />
                  This will permanently delete:
                  <ul className="list-disc list-inside mt-2 text-red-600">
                    <li>The user account</li>
                    <li>All their programs</li>
                    <li>All their athletes</li>
                    <li>All attendance records</li>
                    <li>Subscription data</li>
                  </ul>
                  <br />
                  This action cannot be undone.
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel>Cancel</AlertDialogCancel>
                <AlertDialogAction onClick={handleDeleteUser} className="bg-red-600 hover:bg-red-700">
                  Delete User
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        </TabsContent>

        {/* Coupons Tab */}
        <TabsContent value="coupons" className="space-y-4" data-testid="coupons-tab-content">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{coupons.length} coupon{coupons.length !== 1 ? 's' : ''}</p>
            <Dialog open={isCreateCouponOpen} onOpenChange={setIsCreateCouponOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700" data-testid="create-coupon-btn">
                  <Gift className="h-4 w-4 mr-2" />
                  Create Coupon
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Create New Coupon</DialogTitle>
                  <DialogDescription>Generate a coupon code for Pro subscription upgrades.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleCreateCoupon} className="space-y-4" data-testid="create-coupon-form">
                  <div>
                    <Label htmlFor="coupon-code">Coupon Code (leave blank to auto-generate)</Label>
                    <Input
                      id="coupon-code"
                      data-testid="coupon-code-input"
                      value={couponFormData.code}
                      onChange={(e) => setCouponFormData({ ...couponFormData, code: e.target.value.toUpperCase() })}
                      placeholder="e.g. WELCOME2026"
                      className="uppercase"
                    />
                  </div>
                  <div>
                    <Label htmlFor="discount-type">Discount Type</Label>
                    <Select value={couponFormData.discount_type} onValueChange={(v) => setCouponFormData({ ...couponFormData, discount_type: v })}>
                      <SelectTrigger data-testid="discount-type-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="free_upgrade">Free Pro Upgrade</SelectItem>
                        <SelectItem value="percentage">Percentage Off</SelectItem>
                        <SelectItem value="fixed">Fixed Amount Off</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  {couponFormData.discount_type !== 'free_upgrade' && (
                    <div>
                      <Label htmlFor="discount-value">
                        {couponFormData.discount_type === 'percentage' ? 'Discount (%)' : 'Discount ($)'}
                      </Label>
                      <Input
                        id="discount-value"
                        type="number"
                        min="1"
                        max={couponFormData.discount_type === 'percentage' ? 100 : 29}
                        value={couponFormData.discount_value}
                        onChange={(e) => setCouponFormData({ ...couponFormData, discount_value: Number(e.target.value) })}
                      />
                    </div>
                  )}
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="max-uses">Max Uses</Label>
                      <Input
                        id="max-uses"
                        data-testid="coupon-max-uses-input"
                        type="number"
                        min="1"
                        value={couponFormData.max_uses}
                        onChange={(e) => setCouponFormData({ ...couponFormData, max_uses: Number(e.target.value) })}
                      />
                    </div>
                    <div>
                      <Label htmlFor="valid-days">Valid For (days)</Label>
                      <Input
                        id="valid-days"
                        data-testid="coupon-valid-days-input"
                        type="number"
                        min="1"
                        value={couponFormData.valid_days}
                        onChange={(e) => setCouponFormData({ ...couponFormData, valid_days: Number(e.target.value) })}
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="coupon-desc">Description (optional)</Label>
                    <Input
                      id="coupon-desc"
                      data-testid="coupon-description-input"
                      value={couponFormData.description}
                      onChange={(e) => setCouponFormData({ ...couponFormData, description: e.target.value })}
                      placeholder="e.g. Welcome offer for new coaches"
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsCreateCouponOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-red-600 hover:bg-red-700" data-testid="submit-coupon-btn">Create Coupon</Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {coupons.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Ticket className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No coupons created yet.</p>
                <p className="text-sm text-gray-400 mt-1">Create your first coupon to offer discounts.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {coupons.map((coupon) => (
                <Card key={coupon.id} className={!coupon.is_active ? 'opacity-60' : ''} data-testid={`coupon-card-${coupon.code}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-gray-100 rounded-lg px-3 py-2 font-mono text-lg font-bold tracking-wider">
                          {coupon.code}
                        </div>
                        <div>
                          <p className="font-medium text-gray-800">{coupon.description}</p>
                          <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                            <span>Used: {coupon.times_used}/{coupon.max_uses}</span>
                            <span>Expires: {formatDate(coupon.expires_at)}</span>
                            <Badge className={coupon.is_active ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-600'}>
                              {coupon.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                            {coupon.discount_type === 'free_upgrade' && <Badge className="bg-purple-100 text-purple-800">Free Upgrade</Badge>}
                            {coupon.discount_type === 'percentage' && <Badge variant="outline">{coupon.discount_value}% off</Badge>}
                            {coupon.discount_type === 'fixed' && <Badge variant="outline">${coupon.discount_value} off</Badge>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Button variant="ghost" size="sm" onClick={() => copyToClipboard(coupon.code)} title="Copy code" data-testid={`copy-coupon-${coupon.code}`}>
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleToggleCoupon(coupon.id)} title={coupon.is_active ? 'Deactivate' : 'Activate'} data-testid={`toggle-coupon-${coupon.code}`}>
                          {coupon.is_active ? <XCircle className="h-4 w-4 text-yellow-600" /> : <CheckCircle className="h-4 w-4 text-green-600" />}
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => handleDeleteCoupon(coupon.id)} title="Delete coupon" data-testid={`delete-coupon-${coupon.code}`}>
                          <Trash2 className="h-4 w-4 text-red-500" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Broadcasts Tab */}
        <TabsContent value="broadcasts" className="space-y-4" data-testid="broadcasts-tab-content">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-500">{broadcasts.length} broadcast{broadcasts.length !== 1 ? 's' : ''} sent</p>
            <Dialog open={isBroadcastDialogOpen} onOpenChange={setIsBroadcastDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-red-600 hover:bg-red-700" data-testid="compose-broadcast-btn">
                  <Send className="h-4 w-4 mr-2" />
                  Compose Broadcast
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Send Broadcast Email</DialogTitle>
                  <DialogDescription>Send an email to all users or a specific audience.</DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSendBroadcast} className="space-y-4" data-testid="broadcast-form">
                  <div>
                    <Label htmlFor="broadcast-audience">Target Audience</Label>
                    <Select value={broadcastFormData.target_audience} onValueChange={(v) => setBroadcastFormData({ ...broadcastFormData, target_audience: v })}>
                      <SelectTrigger data-testid="broadcast-audience-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="pro">Pro Users Only</SelectItem>
                        <SelectItem value="free">Free Users Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="broadcast-subject">Subject</Label>
                    <Input
                      id="broadcast-subject"
                      data-testid="broadcast-subject-input"
                      value={broadcastFormData.subject}
                      onChange={(e) => setBroadcastFormData({ ...broadcastFormData, subject: e.target.value })}
                      placeholder="e.g. New Feature Announcement"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="broadcast-message">Message</Label>
                    <Textarea
                      id="broadcast-message"
                      data-testid="broadcast-message-input"
                      value={broadcastFormData.message}
                      onChange={(e) => setBroadcastFormData({ ...broadcastFormData, message: e.target.value })}
                      placeholder="Write your message here..."
                      rows={6}
                      required
                    />
                  </div>
                  <div className="flex justify-end space-x-2">
                    <Button type="button" variant="outline" onClick={() => setIsBroadcastDialogOpen(false)}>Cancel</Button>
                    <Button type="submit" className="bg-red-600 hover:bg-red-700" disabled={sendingBroadcast} data-testid="send-broadcast-btn">
                      {sendingBroadcast ? (
                        <><div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" /> Sending...</>
                      ) : (
                        <><Send className="h-4 w-4 mr-2" /> Send Broadcast</>
                      )}
                    </Button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          </div>

          {broadcasts.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Megaphone className="h-12 w-12 text-gray-300 mx-auto mb-3" />
                <p className="text-gray-500">No broadcasts sent yet.</p>
                <p className="text-sm text-gray-400 mt-1">Compose your first broadcast to reach all users.</p>
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-3">
              {broadcasts.map((b) => (
                <Card key={b.id} data-testid={`broadcast-card-${b.id}`}>
                  <CardContent className="py-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-medium text-gray-800">{b.subject}</p>
                        <div className="flex items-center gap-3 mt-1 text-sm text-gray-500">
                          <span>To: {b.target_audience === 'all' ? 'All Users' : b.target_audience === 'pro' ? 'Pro Users' : 'Free Users'}</span>
                          <span>Recipients: {b.recipients_count || b.sent_count || 0}</span>
                          <span>Sent: {formatDate(b.created_at)}</span>
                          <Badge className={
                            b.status === 'completed' ? 'bg-green-100 text-green-800' :
                            b.status === 'sending' ? 'bg-blue-100 text-blue-800' :
                            'bg-gray-100 text-gray-600'
                          }>
                            {b.status === 'completed' ? 'Sent' : b.status === 'sending' ? 'Sending...' : b.status || 'Unknown'}
                          </Badge>
                        </div>
                        {b.failed_count > 0 && (
                          <p className="text-xs text-red-500 mt-1">{b.failed_count} failed delivery(ies)</p>
                        )}
                      </div>
                      <p className="text-sm text-gray-400">by {b.sent_by}</p>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>

        {/* Card Payments Tab */}
        <TabsContent value="payments" className="space-y-4" data-testid="payments-tab-content">
          <CardPaymentsAdmin />
        </TabsContent>
      </Tabs>
    </div>
  );
};

// Card Payments Admin Sub-Component
const CardPaymentsAdmin = () => {
  const { toast } = useToast();
  const [overview, setOverview] = useState(null);
  const [transactions, setTransactions] = useState([]);
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('');

  useEffect(() => { fetchData(); }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [overviewRes, txnRes, settingsRes] = await Promise.all([
        cardAdminAPI.getOverview(),
        cardAdminAPI.getTransactions({ limit: 50 }),
        cardAdminAPI.getSettings(),
      ]);
      setOverview(overviewRes.data);
      setTransactions(txnRes.data.transactions || []);
      setSettings(settingsRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async (key, currentVal) => {
    try {
      await cardAdminAPI.updateSettings({ [key]: !currentVal });
      toast({ title: 'Setting updated' });
      fetchData();
    } catch (e) {
      toast({ title: 'Failed to update', variant: 'destructive' });
    }
  };

  const handleRefund = async (orderId) => {
    if (!window.confirm('Are you sure you want to refund this payment?')) return;
    try {
      await cardAdminAPI.refund(orderId);
      toast({ title: 'Refund processed' });
      fetchData();
    } catch (e) {
      toast({ title: e.response?.data?.detail || 'Refund failed', variant: 'destructive' });
    }
  };

  const handleTestConnection = async () => {
    try {
      const res = await cardAdminAPI.testConnection();
      toast({ title: res.data.ok ? `Connection OK (${res.data.mode})` : `Failed: ${res.data.detail}`, variant: res.data.ok ? 'default' : 'destructive' });
    } catch (e) {
      toast({ title: 'Connection test failed', variant: 'destructive' });
    }
  };

  if (loading) return <div className="text-center py-8"><Loader2 className="h-8 w-8 animate-spin mx-auto" /></div>;

  const o = overview || {};
  const p = o.payments || {};
  const filteredTxns = filter ? transactions.filter(t => t.status === filter) : transactions;

  return (
    <div className="space-y-4">
      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        {[
          { label: 'Paid', value: p.paid || 0, color: 'text-green-600' },
          { label: 'Failed', value: p.failed || 0, color: 'text-red-600' },
          { label: 'Review', value: p.needs_review || 0, color: 'text-amber-600' },
          { label: 'Revenue', value: `$${(o.revenue?.total_usd || 0).toFixed(2)}`, color: 'text-blue-600' },
        ].map((k, i) => (
          <Card key={i}>
            <CardContent className="pt-4 pb-3 text-center">
              <p className="text-xs text-gray-500">{k.label}</p>
              <p className={`text-2xl font-bold ${k.color}`}>{k.value}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Settings */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Settings className="h-4 w-4" /> Payment Settings
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">Card Payments (Plug'n Pay)</p>
              <p className="text-xs text-gray-500">Mode: {o.mode || 'off'}</p>
            </div>
            <button
              onClick={() => handleToggle('card_checkout_enabled', settings?.card?.checkout_enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings?.card?.checkout_enabled ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="card-payments-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings?.card?.checkout_enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <div className="flex items-center justify-between">
            <div>
              <p className="font-medium text-sm">PayPal Payments</p>
              <p className="text-xs text-gray-500">Toggle PayPal as a payment option</p>
            </div>
            <button
              onClick={() => handleToggle('paypal_enabled', settings?.paypal?.enabled)}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${settings?.paypal?.enabled ? 'bg-green-500' : 'bg-gray-300'}`}
              data-testid="paypal-toggle"
            >
              <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${settings?.paypal?.enabled ? 'translate-x-6' : 'translate-x-1'}`} />
            </button>
          </div>
          <Button size="sm" variant="outline" onClick={handleTestConnection} data-testid="test-connection-btn">
            Test Gateway Connection
          </Button>
        </CardContent>
      </Card>

      {/* Transactions */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Transactions</CardTitle>
            <div className="flex gap-1">
              {['', 'paid', 'failed', 'needs_review', 'processing'].map(f => (
                <button key={f} onClick={() => setFilter(f)} className={`px-2 py-1 text-xs rounded ${filter === f ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-500 hover:bg-gray-100'}`}>
                  {f || 'All'}
                </button>
              ))}
            </div>
          </div>
        </CardHeader>
        <CardContent>
          {filteredTxns.length === 0 ? (
            <p className="text-sm text-gray-500 text-center py-4">No transactions yet</p>
          ) : (
            <div className="space-y-2 max-h-[400px] overflow-y-auto">
              {filteredTxns.map(txn => (
                <div key={txn.order_id} className="flex items-center justify-between p-3 border rounded-lg text-sm" data-testid={`txn-${txn.order_id}`}>
                  <div className="min-w-0">
                    <p className="font-mono text-xs text-gray-400 truncate">{txn.order_id}</p>
                    <p className="font-medium">{txn.product_name} — ${txn.amount?.toFixed(2)} USD</p>
                    <p className="text-xs text-gray-500">{txn.user_email} · {new Date(txn.created_at).toLocaleString()}</p>
                  </div>
                  <div className="flex items-center gap-2 shrink-0 ml-2">
                    <Badge className={
                      txn.status === 'paid' ? 'bg-green-100 text-green-700' :
                      txn.status === 'failed' ? 'bg-red-100 text-red-700' :
                      txn.status === 'needs_review' ? 'bg-amber-100 text-amber-700' :
                      txn.status === 'refunded' ? 'bg-purple-100 text-purple-700' :
                      'bg-gray-100 text-gray-700'
                    }>{txn.status}</Badge>
                    {txn.status === 'paid' && (
                      <Button size="sm" variant="ghost" className="text-red-500 h-7 px-2" onClick={() => handleRefund(txn.order_id)}>
                        Refund
                      </Button>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminPanel;
